import React, { useState, useEffect, useRef, ReactNode } from 'react'
import { Tab } from '@/components/Tab/types'
import { VenueCustomerStatus, VenueStatusData, processData } from './types'
import VenueStatusBanner from './VenueStatusBanner'
import { useAtomValue } from 'jotai'
import {
  venueAtom,
  userInfoAtom,
  venueCustomerAtom,
  venueStatusClaimAtom,
  venueCustomersAtom,
  venueStatusSignupBonusPointsAtom,
} from '@/state/global'

function VenueStatusComponent({
  animateProgressBar = true,
  tab = null,
  showTitle = true,
  mainDivStyle = { marginTop: '24px' },
  onAllAnimationsComplete = () => {},
  children,
}: {
  animateProgressBar?: boolean
  tab?: Tab | null
  showTitle?: boolean
  mainDivStyle?: React.CSSProperties
  onAllAnimationsComplete?: () => void
  children?: ReactNode
}) {
  const venue = useAtomValue(venueAtom)
  const user = useAtomValue(userInfoAtom)
  const emptyVenueCustomerStatus = {
    venue_uuid: venue.venue_uuid,
    venue_status_text_opt_in: undefined,
    vip: false,
    venue_status_points_balance: 0,
    venue_status_redeemable_amount: '0.00',
    venue_status_redeemable_points: 0,
    venue_status_claimable_points: 0,
    venue_status_claimable_amount: '0.00',
    venue_name: venue.name,
    venue_status_vip_points_multiplier: 1,
    venue_status_points_threshold_for_reward: 0,
    venue_status_points_to_next_reward: venue.venue_status_points_threshold_for_reward,
    venue_status_points_toward_next_reward: 0,
    venue_status_reward_dollar_amount: venue.venue_status_reward_dollar_amount,
    venue_status_signup_on: undefined,
  } as VenueCustomerStatus

  const venueCustomers = useAtomValue(venueCustomersAtom)
  const venueCustomer = useAtomValue(venueCustomerAtom)
  const temporaryVenueCustomerStatus =
    venueCustomers.find((vc) => vc.venues.length > 0 && vc.venues[0].venue_uuid === venue.venue_uuid)?.venues[0] ??
    emptyVenueCustomerStatus

  const venueStatusClaim = useAtomValue(venueStatusClaimAtom)
  const venueStatusSignupBonusPoints = useAtomValue(venueStatusSignupBonusPointsAtom)

  const [data, setData] = useState<VenueStatusData>(
    processData(tab, temporaryVenueCustomerStatus, emptyVenueCustomerStatus, venueStatusClaim, venueStatusSignupBonusPoints)
  )

  function NonVenueStatusComponent() {
    return <div style={mainDivStyle}>{children}</div>
  }

  useEffect(() => {
    if (!venueCustomer || !venueCustomer?.venues[0]) return
    setData(
      processData(tab, venueCustomer?.venues[0], temporaryVenueCustomerStatus, venueStatusClaim, venueStatusSignupBonusPoints)
    )
  }, [venueCustomer])

  if (!venue || !user) return <NonVenueStatusComponent />
  if (venue?.venue_status_enabled === false) return <NonVenueStatusComponent />
  if (!venueCustomer || !venueCustomer?.venues[0]) return <NonVenueStatusComponent />

  return (
    <div style={mainDivStyle}>
      {children}
      <VenueStatusBanner
        animated={animateProgressBar}
        points={data.points}
        previousProgress={data.previousPoints ?? 0}
        threshold={data.threshold}
        nextPoints={data.nextPoints}
        title={showTitle ? data.title : undefined}
        rewardDollarAmount={data.rewardDollarAmount}
        redeemedOrEarnedAmount={data.redeemedOrEarnedAmount}
        availableAmount={data.availableAmount}
        werePointsEarned={data.werePointsEarned}
        werePointsRedeemed={data.werePointsRedeemed}
        vip={data.vip}
        vipMultiplier={data.vipMultiplier}
        firstTimeBonusPoints={tab ? data.firstTimeBonusPoints : undefined}
        onAnimationComplete={() => {
          onAllAnimationsComplete?.()
        }}
      />
    </div>
  )
}

export default VenueStatusComponent
